import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreviewModal = ({ isOpen, onClose, formElements, formTitle, paymentConfig }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Payment Form Preview</h3>
            <p className="text-sm text-muted-foreground mt-1">Test your payment form appearance</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-muted/20">
          <div className="max-w-2xl mx-auto">
            {/* Payment Header */}
            <div className="mb-6 p-6 bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-foreground">{formTitle}</h2>
                <div className="flex items-center gap-2">
                  <Icon name="Lock" size={16} className="text-green-600" />
                  <span className="text-xs text-muted-foreground">Secure</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {paymentConfig?.description || 'Complete your secure payment'}
              </p>
            </div>

            {/* Form Elements */}
            <div className="space-y-4 bg-card border border-border rounded-lg p-6">
              {formElements?.length === 0 ? (
                <div className="py-12 text-center">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">
                    No elements added yet. Add elements to see the preview.
                  </p>
                </div>
              ) : (
                formElements?.map((element, index) => (
                  <div key={element?.id || index} className="space-y-2">
                    {element?.label && (
                      <label className="text-sm font-medium text-foreground">
                        {element?.label}
                        {element?.required && <span className="text-destructive ml-1">*</span>}
                      </label>
                    )}
                    <div className="text-sm text-muted-foreground">
                      {element?.type} field preview
                    </div>
                  </div>
                ))
              )}

              {/* Payment Summary */}
              {paymentConfig?.mode === 'fixed' && (
                <div className="pt-4 border-t border-border">
                  <div className="p-4 bg-muted/30 rounded-md space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="text-foreground font-medium">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: paymentConfig?.currency || 'USD',
                        })?.format(paymentConfig?.amount || 0)}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-border flex justify-between">
                      <span className="text-sm font-semibold text-foreground">Total</span>
                      <span className="text-lg font-bold text-primary">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: paymentConfig?.currency || 'USD',
                        })?.format(paymentConfig?.amount || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <button className="w-full py-3 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2">
                  <Icon name="Lock" size={16} />
                  Complete Payment
                </button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  Secured by {paymentConfig?.processor || 'Stripe'} • Your information is encrypted
                </p>
              </div>
            </div>

            {/* Security Badges */}
            <div className="mt-6 flex items-center justify-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Lock" size={14} className="text-green-600" />
                <span>SSL Encrypted</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="ShieldCheck" size={14} className="text-blue-600" />
                <span>Secured by Stripe</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Eye" size={14} className="text-purple-600" />
                <span>Privacy Protected</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              <Icon name="Info" size={14} className="inline mr-1" />
              This is a preview. No actual payment will be processed.
            </div>
            <Button onClick={onClose}>Close Preview</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;